import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, money, formatApiError } from "../../lib/api";

const NeedsReview = ({ items, onDone }) => {
  const act = async (id, action) => {
    try { await api.post(`/admin/pledge/review/${id}`, { action }); toast.success(action === "sent" ? "Marked as sent" : "Will retry on next send"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div data-testid="pledge-needs-review" className="mt-4 border border-amber-300/30 rounded-xl p-4">
      <p className="text-sm text-amber-200">Check these in Pledge before retrying. A send was interrupted, so Pledge may already have the gift.</p>
      {items.map((d) => (
        <div key={d.id} data-testid={`pledge-review-${d.id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t border-white/5 pt-2 mt-2">
          <span>{money(d.amount)} · {d.charity_name || "school"} <span className="text-slate-500 text-xs">ref {d.id}</span></span>
          <span className="flex gap-3">
            <button data-testid={`pledge-review-sent-${d.id}`} onClick={() => act(d.id, "sent")} className="text-emerald-300 text-xs hover:underline">It's in Pledge — mark sent</button>
            <button data-testid={`pledge-review-retry-${d.id}`} onClick={() => act(d.id, "retry")} className="text-[#38BDF8] text-xs hover:underline">Not in Pledge — retry</button>
          </span>
        </div>
      ))}
    </div>
  );
};

export const PledgePanel = ({ schools, onLinked }) => {
  const [s, setS] = useState(null);
  const [q, setQ] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [target, setTarget] = useState("");
  const load = useCallback(() => api.get("/admin/pledge/status").then((r) => setS(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const search = async () => {
    try { setOrgs((await api.get(`/admin/pledge/organizations?q=${encodeURIComponent(q)}`)).data); } catch (e) { toast.error(formatApiError(e)); }
  };
  const link = async (org) => {
    const c = schools.find((x) => x.id === target);
    if (!c) return toast.error("Pick which school to link first");
    const { id, created_at, total_received, ...body } = c;
    try {
      await api.put(`/admin/charities/${id}`, { ...body, pledge_org_id: org.id });
      toast.success(`${c.name} linked to ${org.name}`); onLinked(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const send = async () => {
    try { const r = await api.post("/admin/pledge/send"); toast.success(`Sent ${r.data.sent}, skipped ${r.data.skipped_unmapped} unlinked, ${r.data.failed} failed`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (!s) return null;
  return (
    <div className="lux-card p-6 mt-6" data-testid="pledge-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow">Pledge giving</p>
          <p data-testid="pledge-status" className={`text-sm mt-1 ${s.enabled ? "text-emerald-300" : "text-amber-300"}`}>
            {s.enabled ? `Connected (${s.env})` : "Not connected yet — add your Pledge API key to turn it on"}
          </p>
          <p data-testid="pledge-pending" className="text-xs text-slate-400 mt-1">{s.pending_count} gift{s.pending_count === 1 ? "" : "s"} waiting to send · {money(s.pending_amount)}</p>
        </div>
        <button data-testid="pledge-send-btn" onClick={send} disabled={!s.enabled || !s.pending_count} className="btn-gold px-5 py-2.5 text-sm disabled:opacity-40">Send waiting gifts</button>
      </div>
      {s.needs_review?.length > 0 && <NeedsReview items={s.needs_review} onDone={load} />}
      {s.unmapped_schools.length > 0 && <p data-testid="pledge-unmapped" className="text-xs text-slate-500 mt-3">Not linked to Pledge yet: {s.unmapped_schools.join(", ")}</p>}
      {s.enabled && (
        <div className="mt-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            <select data-testid="pledge-target-school" value={target} onChange={(e) => setTarget(e.target.value)} className="lux-input sm:!w-auto max-w-full">
              <option value="">School to link…</option>
              {schools.filter((c) => c.active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input data-testid="pledge-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Pledge, e.g. Colorado Mountain College Foundation" className="lux-input flex-1 min-w-0 sm:min-w-[240px]" />
            <button data-testid="pledge-search-btn" onClick={search} disabled={q.length < 2} className="btn-ghost px-4 py-2 text-sm">Search</button>
          </div>
          {orgs.map((o) => (
            <div key={o.id} data-testid={`pledge-org-${o.id}`} className="flex items-center justify-between gap-3 text-sm border-t border-white/5 pt-2">
              <span>{o.name} <span className="text-slate-500 text-xs">{o.ngo_id} {o.city ? `· ${o.city}` : ""}</span></span>
              <button data-testid={`pledge-link-${o.id}`} onClick={() => link(o)} className="text-[#38BDF8] text-xs hover:underline">Link</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
